/**
 * Lightweight user presence system via Supabase
 * Uses a dedicated 'user_presence' table with heartbeats
 * Users are "online" if their last heartbeat was within 2 minutes
 */
import { supabase } from '../data/database';

const HEARTBEAT_INTERVAL = 60 * 1000; // 60 seconds
const ONLINE_THRESHOLD = 2 * 60 * 1000; // 2 minutes
let heartbeatTimer = null;

/**
 * Start sending heartbeats for the logged-in user
 */
export function startPresence(user) {
  if (!supabase || !user) return;
  stopPresence();
  sendHeartbeat(user);
  heartbeatTimer = setInterval(() => sendHeartbeat(user), HEARTBEAT_INTERVAL);
}

/**
 * Stop sending heartbeats (on logout)
 */
export function stopPresence() {
  if (heartbeatTimer) {
    clearInterval(heartbeatTimer);
    heartbeatTimer = null;
  }
}

/**
 * Send a single heartbeat
 */
async function sendHeartbeat(user) {
  if (!supabase) return;
  try {
    await supabase.from('user_presence').upsert({
      user_id: user.id,
      user_name: user.name || '',
      user_role: user.role || '',
      last_seen: new Date().toISOString(),
    }, { onConflict: 'user_id' });
  } catch (e) {
    // Silent fail — presence is non-critical
  }
}

/**
 * Get all currently online users
 * @returns {Promise<Array>} List of online users with { user_id, user_name, user_role, last_seen }
 */
export async function getOnlineUsers() {
  if (!supabase) return [];
  try {
    const cutoff = new Date(Date.now() - ONLINE_THRESHOLD).toISOString();
    const { data, error } = await supabase
      .from('user_presence')
      .select('*')
      .gte('last_seen', cutoff);
    if (error || !data) return [];
    return data;
  } catch {
    return [];
  }
}
